import { useEffect, useMemo, useState } from 'react'
import { Button, Dialog, Notice, Progress, Select, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, TextField } from '@/components/play'
import { apiErrors, importAthletes } from '@/lib/athletes/api'
import { FIELD_LABELS, IMPORT_FIELDS, guessMapping, importRows, parseCsv, readFileText, type ColumnMapping, type ImportField } from '@/lib/athletes/csv'
import type { Race, RowResult } from '@/lib/athletes/types'
import { RaceLabel, raceLooks } from './RaceLabel'

interface Props {
  eventId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  races: Race[]
  canTrack: boolean
  onImported: () => void
}

type Step =
  | { kind: 'choose' }
  | { kind: 'map'; fileName: string; rows: string[][] }
  | { kind: 'importing'; done: number; total: number }
  | { kind: 'done'; created: number; updated: number; failed: { line: number; errors: string[] }[]; noted: number }

const UNUSED = 'unused'
const PREVIEW_ROWS = 5
const NOTES_SHOWN = 10

/**
 * A start list in, as a CSV: its columns are guessed from the headers and confirmed here, a few rows previewed with
 * their races matched to the event's courses, then every row sent in batches. Importing the same list again updates
 * athletes by bib (then timing id) instead of adding them twice.
 */
export function AthleteImportDialog({ eventId, open, onOpenChange, races, canTrack, onImported }: Props) {
  const [step, setStep] = useState<Step>({ kind: 'choose' })
  const [mapping, setMapping] = useState<ColumnMapping>({})
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setStep({ kind: 'choose' })
    setMapping({})
    setError('')
  }, [open])

  const rows = useMemo(() => (step.kind === 'map' ? importRows(step.rows, mapping, races) : []), [step, mapping, races])
  const importable = rows.filter((row) => row.importable)
  const notedRows = rows.filter((row) => row.notes.length > 0)
  const noted = notedRows.length
  const looks = useMemo(() => raceLooks(races), [races])

  async function choose(file: File | null) {
    if (!file) return
    setError('')
    try {
      const parsed = parseCsv(await readFileText(file))
      if (parsed.length < 2) return setError('That file has no rows under its header. Choose a CSV start list.')
      setMapping(guessMapping(parsed[0]))
      setStep({ kind: 'map', fileName: file.name, rows: parsed })
    } catch {
      setError('That file couldn’t be read. Choose a CSV start list.')
    }
  }

  async function runImport() {
    if (step.kind !== 'map' || importable.length === 0) return
    setError('')
    const total = importable.length
    setStep({ kind: 'importing', done: 0, total })
    try {
      const results: RowResult[] = await importAthletes(eventId, importable.map((row) => row.fields), (done) =>
        setStep({ kind: 'importing', done, total }),
      )
      setStep({
        kind: 'done',
        created: results.filter((result) => result.status === 'created').length,
        updated: results.filter((result) => result.status === 'updated').length,
        failed: results
          .filter((result) => result.status === 'error')
          .map((result) => ({ line: importable[result.index]?.line ?? result.index + 2, errors: result.errors ?? [] })),
        noted,
      })
      onImported()
    } catch (failure) {
      setError(apiErrors(failure, 'The import stopped. Rows sent before it stopped are saved; import the file again to finish.'))
      onImported()
      setStep({ kind: 'choose' })
    }
  }

  const headers = step.kind === 'map' ? step.rows[0] : []
  const columnItems = [
    { value: UNUSED, label: 'Not in the file' },
    ...headers.map((header, index) => ({ value: String(index), label: header || `Column ${index + 1}` })),
  ]
  const nameMapped = mapping.name !== undefined || mapping.first_name !== undefined || mapping.last_name !== undefined

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="wide"
      title="Import a start list"
      description="A CSV with a row per athlete. Importing it again updates athletes by bib."
      footer={
        step.kind === 'map' ? (
          <>
            <Button variant="secondary" onClick={() => setStep({ kind: 'choose' })}>
              Choose another file
            </Button>
            <Button onClick={() => void runImport()} disabled={importable.length === 0 || !nameMapped}>
              Import {importable.length} {importable.length === 1 ? 'athlete' : 'athletes'}
            </Button>
          </>
        ) : step.kind === 'done' ? (
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        ) : (
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={step.kind === 'importing'}>
            Cancel
          </Button>
        )
      }
    >
      <div className="space-y-5">
        {error && (
          <Notice tone="error" alert>
            {error}
          </Notice>
        )}

        {step.kind === 'choose' && (
          <TextField
            label="Start list"
            type="file"
            accept=".csv,text/csv,text/plain"
            description="Columns such as Name (or First and Last name), Bib, Gender, Race, Tracker id. Comma, semicolon or tab separated."
            onChange={(e) => void choose(e.target.files?.[0] ?? null)}
          />
        )}

        {step.kind === 'map' && (
          <>
            <p className="text-sm">
              {step.fileName}: {rows.length} {rows.length === 1 ? 'row' : 'rows'}
              {noted > 0 && ` · ${noted} with notes`}
              {rows.length - importable.length > 0 && ` · ${rows.length - importable.length} can’t be imported`}
            </p>
            {!nameMapped && <Notice tone="warning">Choose the column with the athletes’ names.</Notice>}
            {mapping.tracker !== undefined && !canTrack && (
              <Notice tone="warning">Connect a tracking provider first, or the tracker ids can’t be saved.</Notice>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {IMPORT_FIELDS.map((field: ImportField) => (
                <Select
                  key={field}
                  label={FIELD_LABELS[field]}
                  size="compact"
                  items={columnItems}
                  value={mapping[field] === undefined ? UNUSED : String(mapping[field])}
                  onValueChange={(value) =>
                    setMapping((now) => {
                      const next = { ...now }
                      if (!value || value === UNUSED) delete next[field]
                      else next[field] = Number(value)
                      return next
                    })
                  }
                />
              ))}
            </div>
            <Table caption={`The first ${Math.min(PREVIEW_ROWS, rows.length)} rows as they’ll be imported`}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell type="number">Row</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell type="data">Bib</TableHeaderCell>
                  <TableHeaderCell>Gender</TableHeaderCell>
                  <TableHeaderCell>Race</TableHeaderCell>
                  <TableHeaderCell type="data">Tracker</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.slice(0, PREVIEW_ROWS).map((row) => (
                  <TableRow key={row.line}>
                    <TableCell type="number">{row.line}</TableCell>
                    <TableCell>{row.fields.name ?? '—'}</TableCell>
                    <TableCell type="data" className="whitespace-nowrap">{row.fields.bib ?? '—'}</TableCell>
                    <TableCell>{row.fields.gender === 'female' ? 'Female' : row.fields.gender === 'male' ? 'Male' : '—'}</TableCell>
                    <TableCell>
                      <RaceLabel look={row.fields.route_id ? looks.get(row.fields.route_id) : undefined} />
                    </TableCell>
                    <TableCell type="data" className="whitespace-nowrap">{row.fields.tracker_ref ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {noted > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Notes</p>
                <ul className="space-y-1 text-sm text-neutral-7">
                  {notedRows.slice(0, NOTES_SHOWN).map((row) => (
                    <li key={row.line}>
                      Row {row.line}: {row.notes.join('. ')}.
                    </li>
                  ))}
                  {noted > NOTES_SHOWN && <li>And {noted - NOTES_SHOWN} more rows with notes.</li>}
                </ul>
              </div>
            )}
          </>
        )}

        {step.kind === 'importing' && <Progress label="Importing athletes" value={step.done} max={step.total} />}

        {step.kind === 'done' && (
          <>
            <Notice tone={step.failed.length ? 'warning' : 'success'}>
              {[
                `${step.created} added`,
                `${step.updated} updated`,
                step.failed.length ? `${step.failed.length} not imported` : null,
                step.noted ? `${step.noted} with notes in the preview` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Notice>
            {step.failed.length > 0 && (
              <ul className="space-y-1 text-sm">
                {step.failed.slice(0, 50).map((failure) => (
                  <li key={failure.line}>
                    Row {failure.line}: {failure.errors.join('. ')}
                  </li>
                ))}
                {step.failed.length > 50 && <li>And {step.failed.length - 50} more.</li>}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}
